import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { api, formatApiError } from '@/App';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import PublicHeader from '@/components/PublicHeader';
import PublicFooter from '@/components/PublicFooter';
import {
  Phone,
  Envelope,
  MapPin,
  Clock,
  FacebookLogo,
  TwitterLogo,
  InstagramLogo
} from '@phosphor-icons/react';

const SERVICE_TYPES = [
  'Water Query',
  'Electricity Query',
  'New Connection',
  'New Account Opening',
  'Payment Arrangement',
  'Building Plans',
  'Tariff Change Application',
  'Billing Query',
  'Value Dispute',
  'Refund Request',
  'Energy Audit',
];

const ContactPage = () => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [formData, setFormData] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    service_interest: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      let recaptcha_token = null;
      if (executeRecaptcha) {
        recaptcha_token = await executeRecaptcha('contact_form');
      }
      await api.post('/leads', { ...formData, recaptcha_token });
      toast.success('Thank you for your enquiry! We will be in touch shortly.');
      setFormData({ company_name: '', contact_name: '', email: '', phone: '', service_interest: '', message: '' });
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <PublicHeader />

      {/* Hero Banner */}
      <section
        className="relative h-[340px] bg-cover bg-center"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80')`,
        }}
      >
        <div className="absolute inset-0 bg-[#4CAF50]/80"></div>
        <div className="relative z-10 h-full flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-5xl sm:text-6xl font-light text-white">Contact</h1>
            <p className="text-white/80 mt-4 text-lg">Get in touch with our team</p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-20 bg-[#4CAF50]" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <div>
              <h2 className="text-3xl font-light text-white mb-8">We are here to help!</h2>

              <div className="space-y-6 text-white/90">
                <div className="flex items-start gap-4">
                  <MapPin className="w-6 h-6 text-white flex-shrink-0 mt-1" />
                  <p>Office no. 555, 5th Floor, 288 on Kent, 288 Kent Avenue, Ferndale, Randburg, Gauteng, 2194</p>
                </div>
                <div className="flex items-center gap-4">
                  <Phone className="w-6 h-6 text-white" />
                  <a href="tel:+27105009477" className="text-white/90 hover:text-white transition-colors underline underline-offset-2" data-testid="contact-phone-link">
                    +27 10 500 9477
                  </a>
                </div>
                <div className="flex items-center gap-4">
                  <Envelope className="w-6 h-6 text-white" />
                  <a href="mailto:info@maremaconsulting.co.za" className="text-white/90 hover:text-white transition-colors underline underline-offset-2" data-testid="contact-email-link">
                    info@maremaconsulting.co.za
                  </a>
                </div>
                <div className="flex items-center gap-4">
                  <Clock className="w-6 h-6 text-white" />
                  <p>Mon - Fri: 08:00 - 16:30</p>
                </div>
              </div>

              <div className="flex gap-4 mt-8">
                <a href="https://www.facebook.com/profile.php?id=100071103276334" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors">
                  <FacebookLogo className="w-5 h-5 text-white" weight="fill" />
                </a>
                <a href="https://x.com/MaremaConsult" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors">
                  <TwitterLogo className="w-5 h-5 text-white" weight="fill" />
                </a>
                <a href="https://www.instagram.com/maremaconsulting/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors">
                  <InstagramLogo className="w-5 h-5 text-white" weight="fill" />
                </a>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white p-8 shadow-xl">
              <h3 className="text-2xl font-semibold text-gray-800 mb-6">Send us a message</h3>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Input
                      placeholder="First Name"
                      value={formData.contact_name.split(' ')[0] || ''}
                      onChange={(e) => {
                        const lastName = formData.contact_name.split(' ').slice(1).join(' ');
                        setFormData({ ...formData, contact_name: `${e.target.value} ${lastName}`.trim() });
                      }}
                      className="border-gray-300 rounded-none bg-gray-50 focus:ring-[#4CAF50] focus:border-[#4CAF50]"
                      data-testid="contact-first-name"
                    />
                  </div>
                  <div>
                    <Input
                      placeholder="Last Name"
                      value={formData.contact_name.split(' ').slice(1).join(' ') || ''}
                      onChange={(e) => {
                        const firstName = formData.contact_name.split(' ')[0] || '';
                        setFormData({ ...formData, contact_name: `${firstName} ${e.target.value}`.trim() });
                      }}
                      className="border-gray-300 rounded-none bg-gray-50 focus:ring-[#4CAF50] focus:border-[#4CAF50]"
                      data-testid="contact-last-name"
                    />
                  </div>
                </div>

                <div>
                  <Input
                    placeholder="Company Name"
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    className="border-gray-300 rounded-none bg-gray-50 focus:ring-[#4CAF50] focus:border-[#4CAF50]"
                    data-testid="contact-company"
                  />
                </div>

                <div>
                  <Input
                    type="email"
                    placeholder="Email *"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="border-gray-300 rounded-none bg-gray-50 focus:ring-[#4CAF50] focus:border-[#4CAF50]"
                    data-testid="contact-email"
                  />
                </div>

                <div>
                  <Input
                    placeholder="Phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="border-gray-300 rounded-none bg-gray-50 focus:ring-[#4CAF50] focus:border-[#4CAF50]"
                    data-testid="contact-phone"
                  />
                </div>

                <div>
                  <Select
                    value={formData.service_interest}
                    onValueChange={(value) => setFormData({ ...formData, service_interest: value })}
                  >
                    <SelectTrigger className="border-gray-300 rounded-none bg-gray-50" data-testid="contact-service-select">
                      <SelectValue placeholder="Issue Experienced *" />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICE_TYPES.map((service) => (
                        <SelectItem key={service} value={service}>{service}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Textarea
                    placeholder="Comment or Message *"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    rows={4}
                    className="border-gray-300 rounded-none bg-gray-50 focus:ring-[#4CAF50] focus:border-[#4CAF50]"
                    data-testid="contact-message"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#4CAF50] hover:bg-[#45a049] text-white rounded-none py-3 font-medium"
                  data-testid="contact-submit-btn"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Google Maps */}
      <section className="w-full" data-testid="google-maps-section">
        <iframe
          title="Marema Consulting Office Location"
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3584.5!2d28.0064!3d-26.0904!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1e957359e4e0b7f7%3A0x8d1e6a0b4c0c0c0c!2s288%20Kent%20Ave%2C%20Ferndale%2C%20Randburg%2C%202194!5e0!3m2!1sen!2sza!4v1700000000000!5m2!1sen!2sza"
          width="100%"
          height="450"
          style={{ border: 0 }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          data-testid="google-maps-iframe"
        ></iframe>
      </section>

      <PublicFooter />
    </div>
  );
};

export default ContactPage;
